// views/roleAllocation/RoleBoard.jsx

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import {
  DragDropContext,
  Droppable,
  Draggable,
} from "@hello-pangea/dnd";

import AssignmentModal from "../../components/AssignmentModal";
import RoleAssignmentDetailsModal from "../../components/RoleAssignmentDetailsModal";
import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import TenantSelect from "../../components/TenantThemeSelect";
import { tokens } from "../../theme/tokens";

import { ROLE_ALLOCATION_VIEW_MODES } from "../../hooks/useRoleAllocationBoard";

const RoleBoard = ({
  roles = [],
  departments = [],
  tenantUsers = [],
  sops = [],

  viewMode,
  setViewMode,

  departmentId,
  setDepartmentId,

  refetch,

  moveTenantUserRoleAssignment,
  setPrimaryTenantUserRoleAssignment,
  removeTenantUserRoleAssignment,

  moveSOPRoleAssignment,
  removeSOPRoleAssignment,

  assignTenantUserToRole,
  assignSOPToRole,
}) => {
  const theme = useTheme();

  const colors = tokens(
    theme.palette.mode
  );

  const [
    boardColumns,
    setBoardColumns,
  ] = useState(roles);

  const [
    assignModalOpen,
    setAssignModalOpen,
  ] = useState(false);

  const [
    assignSOPModalOpen,
    setAssignSOPModalOpen,
  ] = useState(false);

  const [assigning, setAssigning] =
    useState(false);

  const [
    assigningSOP,
    setAssigningSOP,
  ] = useState(false);

  const [
    assignmentDetailsOpen,
    setAssignmentDetailsOpen,
  ] = useState(false);

  const [
    selectedAssignmentCard,
    setSelectedAssignmentCard,
  ] = useState(null);

  const [
    selectedAssignmentRole,
    setSelectedAssignmentRole,
  ] = useState(null);

  const [
    removingAssignment,
    setRemovingAssignment,
  ] = useState(false);

  const [
    settingPrimary,
    setSettingPrimary,
  ] = useState(false);

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setBoardColumns(roles);
  }, [roles]);

  const selectableRoles = useMemo(
    () => {
      if (!departmentId) {
        return boardColumns.map(
          (column) => column.role
        );
      }

      return boardColumns
        .map(
          (column) => column.role
        )
        .filter(
          (role) =>
            role.departmentId ===
            departmentId
        );
    },
    [
      boardColumns,
      departmentId,
    ]
  );

  const assignedSopIds = useMemo(
    () => {
      return boardColumns.flatMap(
        (column) =>
          (
            column.sopCards || []
          ).map(
            (card) =>
              card.sop?.id
          )
      );
    },
    [boardColumns]
  );

  const availableSops = useMemo(
    () => {
      return sops.filter(
        (sop) =>
          !assignedSopIds.includes(
            sop.id
          )
      );
    },
    [
      sops,
      assignedSopIds,
    ]
  );

  const handleOpenAssignmentDetails =
    (card, role) => {
      if (
        viewMode !==
        ROLE_ALLOCATION_VIEW_MODES.PEOPLE
      ) {
        return;
      }

      setSelectedAssignmentCard(
        card
      );

      setSelectedAssignmentRole(
        role
      );

      setAssignmentDetailsOpen(
        true
      );
    };

  const handleCloseAssignmentDetails =
    () => {
      if (
        removingAssignment ||
        settingPrimary
      ) {
        return;
      }

      setAssignmentDetailsOpen(
        false
      );

      setSelectedAssignmentCard(
        null
      );

      setSelectedAssignmentRole(
        null
      );
    };

  const handleAssignPerson =
    async ({
      tenantUserId,
      tenantRoleId,
      isPrimary,
    }) => {
      try {
        setAssigning(true);

        await assignTenantUserToRole({
          tenantUserId,
          tenantRoleId,
          isPrimary,
        });

        setAssignModalOpen(false);

        setSnackbar({
          open: true,
          message:
            "Person assigned successfully",
          severity: "success",
        });

        await refetch();
      } catch (error) {
        console.error(
          "Failed to assign person:",
          error
        );

        setSnackbar({
          open: true,
          message:
            error?.response
              ?.errors?.[0]
              ?.message ||
            "Failed to assign person",
          severity: "error",
        });
      } finally {
        setAssigning(false);
      }
    };

  const handleAssignSOP =
    async ({
      sopId,
      tenantRoleId,
    }) => {
      try {
        setAssigningSOP(true);

        await assignSOPToRole({
          sopId,
          tenantRoleId,
        });

        setAssignSOPModalOpen(
          false
        );

        setSnackbar({
          open: true,
          message:
            "SOP assigned successfully",
          severity: "success",
        });

        await refetch();
      } catch (error) {
        console.error(
          "Failed to assign SOP:",
          error
        );

        setSnackbar({
          open: true,
          message:
            error?.response
              ?.errors?.[0]
              ?.message ||
            "Failed to assign SOP",
          severity: "error",
        });
      } finally {
        setAssigningSOP(
          false
        );
      }
    };

  const handleRemovePersonAssignment =
    async ({
      assignmentId,
    }) => {
      if (!assignmentId) {
        setSnackbar({
          open: true,
          message:
            "Role assignment ID is missing",
          severity: "error",
        });
        return;
      }

      try {
        setRemovingAssignment(
          true
        );

        await removeTenantUserRoleAssignment({
          assignmentId,
        });

        setAssignmentDetailsOpen(
          false
        );

        setSelectedAssignmentCard(
          null
        );

        setSelectedAssignmentRole(
          null
        );

        setSnackbar({
          open: true,
          message:
            "Person removed from role",
          severity: "success",
        });

        await refetch();
      } catch (error) {
        console.error(
          "Failed to remove role assignment:",
          error
        );

        setSnackbar({
          open: true,
          message:
            error?.response
              ?.errors?.[0]
              ?.message ||
            "Failed to remove person from role",
          severity: "error",
        });
      } finally {
        setRemovingAssignment(
          false
        );
      }
    };

  const handleSetPrimaryAssignment =
    async ({
      assignmentId,
    }) => {
      if (!assignmentId) {
        setSnackbar({
          open: true,
          message:
            "Role assignment ID is missing",
          severity: "error",
        });
        return;
      }

      try {
        setSettingPrimary(true);

        await setPrimaryTenantUserRoleAssignment({
          assignmentId,
        });

        setAssignmentDetailsOpen(
          false
        );

        setSelectedAssignmentCard(
          null
        );

        setSelectedAssignmentRole(
          null
        );

        setSnackbar({
          open: true,
          message:
            "Primary role updated",
          severity: "success",
        });

        await refetch();
      } catch (error) {
        console.error(
          "Failed to set primary role:",
          error
        );

        setSnackbar({
          open: true,
          message:
            error?.response
              ?.errors?.[0]
              ?.message ||
            "Failed to update primary role",
          severity: "error",
        });
      } finally {
        setSettingPrimary(
          false
        );
      }
    };

  const handleRemoveSOPAssignment =
    async ({
      assignmentId,
    }) => {
      if (
        !assignmentId ||
        !removeSOPRoleAssignment
      ) {
        return;
      }

      try {
        await removeSOPRoleAssignment({
          assignmentId,
        });

        setSnackbar({
          open: true,
          message:
            "SOP removed from role",
          severity: "success",
        });

        await refetch();
      } catch (error) {
        console.error(
          "Failed to remove SOP assignment:",
          error
        );

        setSnackbar({
          open: true,
          message:
            error?.response
              ?.errors?.[0]
              ?.message ||
            "Failed to remove SOP from role",
          severity: "error",
        });
      }
    };

  const handleViewModeChange = (
    _,
    nextMode
  ) => {
    if (!nextMode) {
      return;
    }

    setViewMode(nextMode);
  };

  const handleDepartmentChange = (
    event
  ) => {
    const value =
      event.target.value;

    setDepartmentId(
      value || null
    );
  };

  const getCardsForColumn = (
    column
  ) => {
    if (
      viewMode ===
      ROLE_ALLOCATION_VIEW_MODES.PEOPLE
    ) {
      return (
        column.peopleCards ||
        []
      );
    }

    if (
      viewMode ===
      ROLE_ALLOCATION_VIEW_MODES.SOPS
    ) {
      return (
        column.sopCards ||
        []
      );
    }

    return [];
  };

  const getCardId = (
    card
  ) => {
    return (
      card.assignment?.id ||
      card.id
    );
  };

  const getCardTitle = (
    card
  ) => {
    if (
      viewMode ===
      ROLE_ALLOCATION_VIEW_MODES.PEOPLE
    ) {
      return (
        card.displayName ||
        "Unknown user"
      );
    }

    if (
      viewMode ===
      ROLE_ALLOCATION_VIEW_MODES.SOPS
    ) {
      return (
        card.sop?.title ||
        "Untitled SOP"
      );
    }

    return "Card";
  };

  const getCardSubtitle = (
    card
  ) => {
    if (
      viewMode ===
      ROLE_ALLOCATION_VIEW_MODES.PEOPLE
    ) {
      return card.isPrimary
        ? "Primary role"
        : "Secondary role";
    }

    return "";
  };

  const getColumnLoadTone = (
    count
  ) => {
    if (count >= 10) {
      return colors
        .redAccent[500];
    }

    if (count >= 6) {
      return (
        colors.orangeAccent?.[500] ||
        colors.grey[500]
      );
    }

    return "var(--tenant-primary)";
  };

  const handleDragEnd =
    async (result) => {
      const {
        source,
        destination,
      } = result;

      if (!destination) {
        return;
      }

      if (
        source.droppableId ===
          destination.droppableId &&
        source.index ===
          destination.index
      ) {
        return;
      }

      const sourceColumnIndex =
        boardColumns.findIndex(
          (column) =>
            column.role.id ===
            source.droppableId
        );

      const destinationColumnIndex =
        boardColumns.findIndex(
          (column) =>
            column.role.id ===
            destination.droppableId
        );

      if (
        sourceColumnIndex === -1 ||
        destinationColumnIndex ===
          -1
      ) {
        return;
      }

      const sourceColumn =
        boardColumns[
          sourceColumnIndex
        ];

      const destinationColumn =
        boardColumns[
          destinationColumnIndex
        ];

      const sourceCards = [
        ...getCardsForColumn(
          sourceColumn
        ),
      ];

      const destinationCards =
        source.droppableId ===
        destination.droppableId
          ? sourceCards
          : [
              ...getCardsForColumn(
                destinationColumn
              ),
            ];

      const [movedCard] =
        sourceCards.splice(
          source.index,
          1
        );

      if (!movedCard) {
        return;
      }

      movedCard.assignment = {
        ...movedCard.assignment,
        tenantRoleId:
          destination.droppableId,
      };

      movedCard.tenantRole =
        destinationColumn.role;

      destinationCards.splice(
        destination.index,
        0,
        movedCard
      );

      const updatedColumns = [
        ...boardColumns,
      ];

      if (
        viewMode ===
        ROLE_ALLOCATION_VIEW_MODES.PEOPLE
      ) {
        updatedColumns[
          sourceColumnIndex
        ] = {
          ...sourceColumn,
          peopleCards:
            sourceCards,
          peopleCount:
            source.droppableId ===
            destination.droppableId
              ? destinationCards.length
              : sourceCards.length,
        };

        updatedColumns[
          destinationColumnIndex
        ] = {
          ...destinationColumn,
          peopleCards:
            destinationCards,
          peopleCount:
            destinationCards.length,
        };
      }

      if (
        viewMode ===
        ROLE_ALLOCATION_VIEW_MODES.SOPS
      ) {
        updatedColumns[
          sourceColumnIndex
        ] = {
          ...sourceColumn,
          sopCards: sourceCards,
          sopCount:
            source.droppableId ===
            destination.droppableId
              ? destinationCards.length
              : sourceCards.length,
        };

        updatedColumns[
          destinationColumnIndex
        ] = {
          ...destinationColumn,
          sopCards:
            destinationCards,
          sopCount:
            destinationCards.length,
        };
      }

      setBoardColumns(
        updatedColumns
      );

      try {
        if (
          viewMode ===
          ROLE_ALLOCATION_VIEW_MODES.PEOPLE
        ) {
          await moveTenantUserRoleAssignment({
            assignmentId:
              movedCard.assignment.id,
            tenantRoleId:
              destination.droppableId,
          });
        }

        if (
          viewMode ===
          ROLE_ALLOCATION_VIEW_MODES.SOPS
        ) {
          await moveSOPRoleAssignment({
            assignmentId:
              movedCard.assignment.id,
            tenantRoleId:
              destination.droppableId,
          });
        }

        setSnackbar({
          open: true,
          message:
            "Assignment updated",
          severity: "success",
        });

        await refetch();
      } catch (error) {
        console.error(
          "Failed to move card:",
          error
        );

        setSnackbar({
          open: true,
          message:
            error?.response
              ?.errors?.[0]
              ?.message ||
            "Failed to update assignment",
          severity: "error",
        });

        await refetch();
      }
    };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={3}
      >
        <Header
          title="ROLE ALLOCATION"
          subtitle="View people and SOP responsibilities by role"
        />

        {viewMode ===
          ROLE_ALLOCATION_VIEW_MODES.SOPS &&
          availableSops.length ===
            0 && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              All SOPs are already
              assigned
            </Typography>
          )}

        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
        >
          <FormControl
            size="small"
            sx={{
              minWidth: 220,
            }}
          >
            <InputLabel id="department-filter-label">
              Department
            </InputLabel>

            <TenantSelect
              label="Department"
              value={
                departmentId || ""
              }
              onChange={
                handleDepartmentChange
              }
            >
              <MenuItem value="">
                All Departments
              </MenuItem>

              {departments.map(
                (department) => (
                  <MenuItem
                    key={
                      department.id
                    }
                    value={
                      department.id
                    }
                  >
                    {
                      department.name
                    }
                  </MenuItem>
                )
              )}
            </TenantSelect>
          </FormControl>

          <ToggleButtonGroup
            exclusive
            value={viewMode}
            onChange={
              handleViewModeChange
            }
            size="small"
          >
            <ToggleButton
              value={
                ROLE_ALLOCATION_VIEW_MODES.PEOPLE
              }
            >
              People
            </ToggleButton>

            <ToggleButton
              value={
                ROLE_ALLOCATION_VIEW_MODES.SOPS
              }
            >
              SOPs
            </ToggleButton>
          </ToggleButtonGroup>

          <ActionButton
            variant="outlined"
            onClick={refetch}
            sx={{
              borderColor:
                "var(--tenant-primary)",
              color:
                "var(--tenant-primary)",

              "&:hover": {
                borderColor:
                  "var(--tenant-secondary)",
                color:
                  "var(--tenant-secondary)",
              },
            }}
          >
            Refresh
          </ActionButton>

          {viewMode ===
            ROLE_ALLOCATION_VIEW_MODES.PEOPLE && (
            <ActionButton
              tone="gold"
              onClick={() =>
                setAssignModalOpen(
                  true
                )
              }
            >
              + Assign Person
            </ActionButton>
          )}

          {viewMode ===
            ROLE_ALLOCATION_VIEW_MODES.SOPS && (
            <ActionButton
              tone="gold"
              onClick={() =>
                setAssignSOPModalOpen(
                  true
                )
              }
              disabled={
                availableSops.length ===
                0
              }
            >
              + Assign SOP
            </ActionButton>
          )}
        </Box>
      </Box>

      <DragDropContext
        onDragEnd={handleDragEnd}
      >
        <Box
          display="flex"
          gap={2}
          p={2}
          overflow="auto"
          minHeight="65vh"
          sx={{
            bgcolor:
              colors.surface.base,
            border: `1px solid ${colors.surface.border}`,
            borderRadius: 3,
          }}
        >
          {boardColumns.map(
            (column) => {
              const cards =
                getCardsForColumn(
                  column
                );

              const count =
                viewMode ===
                ROLE_ALLOCATION_VIEW_MODES.PEOPLE
                  ? column.peopleCount
                  : column.sopCount;

              const emphasisColor =
                getColumnLoadTone(
                  count
                );

              return (
                <Droppable
                  droppableId={
                    column.role.id
                  }
                  key={
                    column.role.id
                  }
                >
                  {(provided) => (
                    <Box
                      ref={
                        provided.innerRef
                      }
                      {...provided.droppableProps}
                      flex={1}
                      minWidth={280}
                      maxWidth="100%"
                      p={2}
                      sx={{
                        flexBasis: 0,
                        bgcolor:
                          colors.surface
                            .card,
                        border: `1px solid ${colors.surface.border}`,
                        borderRadius: 3,
                        transition:
                          "border-color 180ms ease, background-color 180ms ease",

                        "&:hover": {
                          borderColor:
                            emphasisColor,
                          bgcolor:
                            colors.surface
                              .raised,
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                        pb={1.25}
                        sx={{
                          borderBottom: `1px solid ${colors.surface.border}`,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              color:
                                colors.text
                                  .primary,
                              fontWeight:
                                700,
                            }}
                          >
                            {
                              column.role
                                .name
                            }
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                colors.text
                                  .secondary,
                            }}
                          >
                            {count}{" "}
                            {viewMode ===
                            ROLE_ALLOCATION_VIEW_MODES.PEOPLE
                              ? "people"
                              : "SOPs"}
                          </Typography>
                        </Box>
                      </Box>

                      {cards.map(
                        (
                          card,
                          index
                        ) => {
                          const cardId =
                            getCardId(
                              card
                            );

                          return (
                            <Draggable
                              draggableId={
                                cardId
                              }
                              index={
                                index
                              }
                              key={
                                cardId
                              }
                            >
                              {(
                                providedDraggable
                              ) => (
                                <Paper
                                  ref={
                                    providedDraggable.innerRef
                                  }
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                  elevation={
                                    0
                                  }
                                  onClick={() =>
                                    handleOpenAssignmentDetails(
                                      card,
                                      column.role
                                    )
                                  }
                                  sx={{
                                    position:
                                      "relative",
                                    p: 2,
                                    mb: 2,
                                    borderRadius:
                                      2,
                                    bgcolor:
                                      colors
                                        .surface
                                        .raised,
                                    color:
                                      colors
                                        .text
                                        .primary,
                                    border: `1px solid ${colors.surface.border}`,
                                    cursor:
                                      viewMode ===
                                      ROLE_ALLOCATION_VIEW_MODES.PEOPLE
                                        ? "pointer"
                                        : "grab",
                                    transition:
                                      "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease",

                                    "&:hover":
                                      {
                                        transform:
                                          "translateY(-2px)",
                                        borderColor:
                                          emphasisColor,
                                        bgcolor:
                                          colors
                                            .surface
                                            .elevated,
                                        boxShadow:
                                          "0 14px 32px rgba(0,0,0,0.28)",
                                      },

                                    "&:active":
                                      {
                                        cursor:
                                          "grabbing",
                                      },
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      viewMode ===
                                      ROLE_ALLOCATION_VIEW_MODES.PEOPLE
                                        ? "View assignment details"
                                        : ""
                                    }
                                    arrow
                                  >
                                    <Box>
                                      <Typography
                                        fontWeight={
                                          700
                                        }
                                        fontSize="0.95rem"
                                        sx={{
                                          mb: 0.5,
                                        }}
                                      >
                                        {getCardTitle(
                                          card
                                        )}
                                      </Typography>

                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color:
                                            colors
                                              .text
                                              .secondary,
                                        }}
                                      >
                                        {getCardSubtitle(
                                          card
                                        )}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </Paper>
                              )}
                            </Draggable>
                          );
                        }
                      )}

                      {
                        provided.placeholder
                      }
                    </Box>
                  )}
                </Droppable>
              );
            }
          )}
        </Box>
      </DragDropContext>

      <AssignmentModal
        open={assignModalOpen}
        onClose={() =>
          setAssignModalOpen(
            false
          )
        }
        onSubmit={({
          entityId,
          roleId,
          isPrimary,
        }) =>
          handleAssignPerson({
            tenantUserId:
              entityId,
            tenantRoleId:
              roleId,
            isPrimary,
          })
        }
        loading={assigning}
        entities={tenantUsers}
        roles={selectableRoles}
        entityLabel="User"
        getEntityLabel={(
          user
        ) => user.displayName}
        requirePrimary
      />

      <AssignmentModal
        open={
          assignSOPModalOpen
        }
        onClose={() =>
          setAssignSOPModalOpen(
            false
          )
        }
        onSubmit={({
          entityId,
          roleId,
        }) =>
          handleAssignSOP({
            sopId: entityId,
            tenantRoleId:
              roleId,
          })
        }
        loading={assigningSOP}
        entities={availableSops}
        roles={selectableRoles}
        entityLabel="SOP"
        getEntityLabel={(
          sop
        ) => sop.title}
        requirePrimary={false}
      />

      <RoleAssignmentDetailsModal
        open={
          assignmentDetailsOpen
        }
        onClose={
          handleCloseAssignmentDetails
        }
        card={
          selectedAssignmentCard
        }
        role={
          selectedAssignmentRole
        }
        onRemove={
          handleRemovePersonAssignment
        }
        onSetPrimary={
          handleSetPrimaryAssignment
        }
        removing={
          removingAssignment
        }
        settingPrimary={
          settingPrimary
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar(
            (current) => ({
              ...current,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() =>
            setSnackbar(
              (current) => ({
                ...current,
                open: false,
              })
            )
          }
          severity={
            snackbar.severity
          }
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleBoard;